import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Input, DatePicker, Select, Button, message, InputNumber, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import api from '../../api/http';
import { useAuth } from '../../hooks/useAuth';

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

const OvertimeApplicationForm = ({ onSubmitted, onClose } = {}) => {
  // 组件逻辑保持不变
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [overtimeTypes, setOvertimeTypes] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState('');
  const [overtimeFilter, setOvertimeFilter] = useState('');
  const [filteredOvertimeTypes, setFilteredOvertimeTypes] = useState([]);

  // 初始化数据
  useEffect(() => {
    // 直接定义加班类型，按照指定顺序：工作日加班、休息日加班、法定节假日加班
    const fixedOvertimeTypes = [
      { label: '工作日加班', value: 'weekday', description: '工作日下班后的加班' },
      { label: '休息日加班', value: 'weekend', description: '周六、周日等休息日加班' },
      { label: '法定节假日加班', value: 'holiday', description: '法定节假日加班' }
    ];
    
    setOvertimeTypes(fixedOvertimeTypes);
    
    const fetchInitialData = async () => {
      setEmployeesLoading(true);
      setEmployeesError('');
      try {
        let formattedEmployees = [];
        const empResponse = await api.get('/employees');
        if (empResponse && empResponse.data) {
          formattedEmployees = empResponse.data
            .filter(emp => emp && emp.name)
            .map(emp => ({
              label: emp.name,
              value: emp.id || emp.employee_id,
              name: emp.name,
              department: emp.department,
              section: emp.section
            }));
          setEmployees(formattedEmployees);
          setFilteredEmployees(formattedEmployees);
        }

        if (user) {
          const userId = user.employee_id || user.id || user.user_id || user.employeeID;
          const department = user.department || user.department_id || user.dept_id || user.dept;

          if (userId) {
            const matchedEmployee = formattedEmployees.find(emp => String(emp.value) === String(userId));
            if (matchedEmployee) {
              form.setFieldsValue({
                employee_id: matchedEmployee.value,
                department: matchedEmployee.department || department || ''
              });
            } else if (department) {
              form.setFieldsValue({ department: department || '' });
            }
          }
        }
      } catch (error) {
        console.error('获取初始化数据失败:', error);
        message.error('获取初始化数据失败');
        if (error.response && error.response.config && error.response.config.url === '/employees') {
          setEmployeesError('获取员工列表失败: ' + (error.message || '未知错误'));
          const mockEmployees = [
            { label: '张三', value: '1' },
            { label: '李四', value: '2' },
            { label: '王五', value: '3' },
            { label: '赵六', value: '4' },
            { label: '钱七', value: '5' }
          ];
          setEmployees(mockEmployees);
          setFilteredEmployees(mockEmployees);
          message.warning('员工数据加载失败，已使用模拟数据');
        }
      } finally {
        setEmployeesLoading(false);
      }
    };

    fetchInitialData();
  }, [user, form]);

  // 处理员工搜索
  const handleEmployeeSearch = useCallback(async (value) => {
    if (!value.trim()) {
      setFilteredEmployees(employees);
      setEmployeesError('');
      return;
    }
    
    setEmployeesLoading(true);
    try {
      const response = await api.get(`/employees?name=${encodeURIComponent(value.trim())}`);
      if (response && response.data && Array.isArray(response.data)) {
        const filtered = response.data.map(emp => ({
          value: emp.id,
          label: `${emp.name} - ${emp.department || ''}${emp.section ? '-' + emp.section : ''}`,
          name: emp.name,
          department: emp.department,
          section: emp.section
        }));
        setFilteredEmployees(filtered);
        setEmployeesError('');
        
      } else {
        setFilteredEmployees([]);
      }
    } catch (error) {
      console.error('搜索员工失败:', error);
      setEmployeesError('搜索员工失败，请稍后重试');
      setFilteredEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  }, [employees]);

  // 处理员工选择
  const handleEmployeeSelect = (value, option) => {
    if (!option) return;
    
    // 选择员工后自动填充部门信息
    form.setFieldsValue({
      department: option.department || ''
    });
  };

  // 文件上传处理
  const handleUploadChange = ({ fileList }) => {
    setUploadedFiles(fileList);
  };

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // 准备提交数据（使用JSON格式）
      if (!values.employee_id) {
        message.error('请选择有效的员工');
        setLoading(false);
        return;
      }

      const submitData = {
        overtime_type: values.overtime_type,
        start_date: values.date_range[0].format('YYYY-MM-DD'),
        end_date: values.date_range[1].format('YYYY-MM-DD'),
        start_time: `${values.start_time || 18}:00`, // 确保是时间格式
        end_time: `${values.end_time || 21}:00`,     // 确保是时间格式
        reason: values.reason,
        work_content: values.project_name || values.reason,
        employee_id: values.employee_id
      };

      // 提交加班申请（简化版，暂时不处理附件）
      const response = await api.post('/leave-overtime/overtime', submitData);
      const payload = response?.data || response;
      const successMsg = payload?.message;
      if (successMsg === '加班申请提交成功') {
        message.success(successMsg);
        form.resetFields();
        setUploadedFiles([]);
        if (typeof onSubmitted === 'function') onSubmitted();
        if (typeof onClose === 'function') onClose();
        else navigate('/daily/leave-overtime');
      } else {
        message.error(payload?.error || successMsg || '加班申请提交失败');
      }
    } catch (error) {
      console.error('提交加班申请失败:', error);
      message.error('提交加班申请失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overtime-application-container">
    <Card title="加班申请" className="overtime-application-form">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          start_time: 18,
          end_time: 21
        }}
      >
        <Form.Item
          label="申请人信息"
          className="section-title"
        >
          <div className="info-section">
            <div className="info-item">
              <Form.Item
                name="employee_id"
                label="申请人姓名"
                className="info-item"
                rules={[{ required: true, message: '请选择申请人' }]}
              >
                <Select
                  placeholder="输入姓名检索"
                  showSearch
                  loading={employeesLoading}
                  onSearch={handleEmployeeSearch}
                  onChange={handleEmployeeSelect}
                  notFoundContent={employeesError ? employeesError : null}
                  filterOption={false}
                >
                  {filteredEmployees.map(emp => (
                    <Option key={emp.value} value={emp.value} {...emp}>
                      {emp.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div className="info-item">
              <Form.Item
                name="department"
                label="部门"
                className="info-item"
              >
                <Input placeholder="部门" disabled />
              </Form.Item>
            </div>
          </div>
        </Form.Item>

        <Form.Item
          label="加班信息"
          className="section-title"
        >
          <Form.Item
            name="overtime_type"
            label="加班类型"
            rules={[{ required: true, message: '请选择加班类型' }]}
          >
            <Select 
              placeholder="请输入关键词搜索加班类型（如工作日、周末等）"
              showSearch
              onSearch={(value) => {
                setOvertimeFilter(value);
                // 根据输入内容过滤加班类型
                if (!value.trim()) {
                  setFilteredOvertimeTypes([]);
                } else {
                  const filtered = overtimeTypes.filter(type => {
                    const searchText = value.toLowerCase();
                    return (
                      type.label.toLowerCase().includes(searchText) ||
                      type.description.toLowerCase().includes(searchText) ||
                      type.value.toLowerCase().includes(searchText)
                    );
                  });
                  setFilteredOvertimeTypes(filtered);
                }
              }}
              filterOption={(input, option) => {
                // 自定义过滤逻辑，支持根据标签、描述或值进行过滤
                const searchText = input.toLowerCase();
                return (
                  option.label.toLowerCase().includes(searchText) ||
                  (option.data && option.data.description && 
                   option.data.description.toLowerCase().includes(searchText)) ||
                  option.value.toLowerCase().includes(searchText)
                );
              }}
            >
              {(overtimeFilter ? filteredOvertimeTypes : overtimeTypes).map(type => (
                <Option 
                  key={type.value} 
                  value={type.value} 
                  data={type}
                >
                  <div>
                    <div style={{ fontWeight: '500' }}>{type.label}</div>
                    {type.description && (
                      <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '2px' }}>
                        {type.description}
                      </div>
                    )}
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="date_range"
            label="加班日期范围"
            rules={[{ required: true, message: '请选择加班日期范围' }]}
            getValueFromEvent={(dates) => dates}
          >
            <RangePicker style={{ width: '100%' }} placeholder={['开始日期', '结束日期']} />
          </Form.Item>

          <div className="time-row">
            <Form.Item
              name="start_time"
              label="每日开始时间"
              rules={[{ required: true, message: '请输入每日开始时间' }]}
              className="time-item"
            >
              <InputNumber 
                min={0} 
                max={24} 
                placeholder="开始时间"
                addonAfter="点"
              />
            </Form.Item>
            <Form.Item
              name="end_time"
              label="每日结束时间"
              rules={[{ required: true, message: '请输入每日结束时间' }]}
              className="time-item"
            >
              <InputNumber 
                min={0} 
                max={24} 
                placeholder="结束时间"
                addonAfter="点"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="project_name"
            label="项目名称"
          >
            <Input placeholder="请输入项目名称" />
          </Form.Item>

          <Form.Item
            name="reason"
            label="加班原因"
            rules={[{ required: true, message: '请输入加班原因' }]}
          >
            <TextArea rows={4} placeholder="请输入加班原因" autoSize={{ minRows: 4 }} />
          </Form.Item>
        </Form.Item>

        <Form.Item
          label="附件上传"
          className="section-title"
        >
          <Form.Item
            name="attachments"
            valuePropName="fileList"
            getValueFromEvent={handleUploadChange}
          >
            <Upload.Dragger 
              listType="picture-card" 
              beforeUpload={() => false}
              multiple
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">
                支持单个或批量上传，可上传工作证明等文件
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Form.Item>

        <Form.Item className="form-actions">
          <Button type="default" onClick={() => navigate(-1)}>取消</Button>
          <Button type="primary" htmlType="submit" loading={loading}>提交申请</Button>
        </Form.Item>
      </Form>
    </Card>
    
    <style jsx>{`
      .section-title {
        font-weight: bold;
        margin-bottom: 16px;
        color: #262626;
        border-left: 4px solid #1890ff;
        padding-left: 12px;
        font-size: 16px;
      }
      
      /* 信息区块统一布局样式 */
      .info-section {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-bottom: 8px;
      }
      .info-item {
        flex: 1;
        min-width: calc(33.333% - 16px);
        display: flex;
        flex-direction: column;
      }
      
      /* 确保表单标签样式一致 */
      .ant-form-item-label > label {
        font-weight: 500;
        color: #262626;
        font-size: 14px;
      }
      
      /* 确保输入框和选择器样式一致 */
      .ant-input,
      .ant-select,
      .ant-picker,
      .ant-input-number {
        border-radius: 4px;
        transition: all 0.3s;
      }
      
      /* 确保tooltip样式一致 */
      .ant-form-item-tooltip {
        font-size: 12px;
      }
      
      /* 响应式设计 */
      @media (max-width: 768px) {
        .info-section {
          flex-direction: column;
        }
        .info-item {
          min-width: 100%;
        }
      }
      
      /* 时间行样式 */
      .time-row {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;
      }
      
      .time-item {
        flex: 1;
        min-width: calc(50% - 16px);
      }
      
      /* 表单操作按钮样式 */
      .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 24px;
      }
      
      /* 加班申请表单容器样式 */
      .overtime-application-form {
        max-width: 100%;
        margin: 0 auto;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
      
      @media (min-width: 769px) and (max-width: 1024px) {
        .overtime-application-form {
          max-width: 90%;
          margin: 0 auto;
        }
      }
      
      @media (min-width: 1025px) {
        .overtime-application-form {
          max-width: 800px;
          margin: 0 auto;
        }
      }
    `}</style>
    </div>
  );
};

export default OvertimeApplicationForm;